<template>
  <div :id="carouselId" class="main-image-carousel">
    <div v-if="currentPage === 1">
      <transition name="fade">
        <ProductImagesCta :product-id="productId" />
      </transition>
    </div>
    <div v-if="(isMobile || isImageIndicatorStylingEnabled) && isAddToListVisible && !isARIndicatorSelected">
      <AddToList :container-id="containerId" />
    </div>

    <button
      v-if="isImageIndicatorStylingEnabled && !isMobile && !isVideoSelected && !isARIndicatorSelected"
      data-test-id="fullscreen-entry"
      tabindex="0"
      class="overlay-entry zoom-entry"
      :class="{
        'zoom-redesign': isImageIndicatorExpEnabled,
        'zoom-first': !isAddToListVisible,
      }"
      aria-label="Open full screen image gallery"
      @click="openImageOverlay"
      @keydown.space.prevent="openImageOverlay"
    >
      <div class="full-screen-dark-medium" aria-hidden="true"></div>
    </button>
    <div v-if="isImageEntryEnabled || isImageEntryEnabled2">
      <transition name="fade">
        <SimilarItemsButton v-show="showSimilarItemsButton" :current-page="currentPage" />
      </transition>
    </div>
    <template v-if="isAddToListVisible && !isMobile && !isImageIndicatorStylingEnabled">
      <AddToList :container-id="containerId" />
    </template>

    <div v-if="isImageIndicatorStylingEnabled">
      <div v-show="currentPage <= CTA_THRESHOLD_VISIBILITY && currentPageOnHover <= CTA_THRESHOLD_VISIBILITY && !isVideoSelected">
        <transition name="fade">
          <OutfittingCta />
        </transition>
      </div>
    </div>

    <MainImageSlideshow
      show-shop-similar
      :in-overlay="false"
      :carousel-images="carouselImages"
      :current-page="currentPage"
      :first-alt-dot="firstAltDot"
      :current-page-on-hover="currentPageOnHover"
      :show-indicator="showIndicator"
      @update-carousel="handleUpdateCarousel"
      @update-first-alt-dot="updateFirstAltDot"
      @image-click="openImageOverlay"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import SimilarItemsButton from '~/components/ctaButtons/SimilarItemsButton.vue';
import ProductImagesCta from '~/components/ProductImagesCta.vue';
import AddToList from '~/modules/add-to-list/components/AddToList.vue';
import MainImageSlideshow from '~/components/common/Media/MainImageSlideshow.vue';
import OutfittingCta from '~/components/ctaButtons/OutfittingCta.vue';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useModal } from '~/composables/utils/useModal';
import useImages from '~/composables/images/useImages';
import useExperiments from '~/stores/useExperimentsStore';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import once from '~/utils/once';
import { useImageryState } from '~/composables/useImageryState';
import { tagOverlayOpened } from '~/features/analytics/image';
import { useProduct } from '~/modules/product/composables/useProduct';

const CTA_THRESHOLD_VISIBILITY = 2;

const { showIndicator = true } = defineProps<{
  showIndicator?: boolean;
}>();

const { currentPage, currentPageOnHover, updateOverlayOpen, firstAltDot, updateCarousel, updateFirstAltDot } = useImageryState();

const handleUpdateCarousel = (index: number) => {
  updateCarousel(index);
};

const { isMobile } = useDeviceType();

const { productId } = useProduct();

const { carouselImages, videoIndex, indicatorIndex } = useImages();
const { isAddToListVisible, isTopLevelZoomerClick } = useFeatureEligibility();
const carouselId = `main-image-carousel-${productId}`;

const containerId = `main-image-${productId}`;
const { open, close } = useModal();

const {
  experimentFlags: { isImageEntryEnabled, isImageEntryEnabled2, isImageIndicatorStylingEnabled, isImageARIndicatorEnabled },
} = useExperiments();

const isImageIndicatorExpEnabled = isImageIndicatorStylingEnabled || isImageARIndicatorEnabled;

const fireTagOverlayOpenedOnce = once(tagOverlayOpened);

const openImageOverlay = (event: MouseEvent) => {
  const target = event?.currentTarget as HTMLElement | null;
  if (!isMobile.value && isTopLevelZoomerClick && !target?.classList?.contains('overlay-entry')) return;
  if (target?.classList?.contains('overlay-entry')) {
    fireTagOverlayOpenedOnce();
  }

  history.pushState({ imageOverlayOpen: true }, '');
  updateOverlayOpen(true);
  open('main-image-overlay');
};

const isVideoSelected = computed(() => currentPage.value - 1 === videoIndex.value || currentPageOnHover.value === videoIndex.value);
const isARIndicatorSelected = computed(() => currentPage.value - 1 === indicatorIndex.value || currentPageOnHover.value === indicatorIndex.value);
const showSimilarItemsButton = computed(() => {
  const isOneImage = carouselImages.value.filter((image) => !image.video).length === 1;
  return !isVideoSelected.value && (((currentPage.value === 2 || currentPage.value === 3) && !isOneImage) || (currentPage.value === 1 && isOneImage));
});

function onPopState() {
  const mainImageOverlayEl = document.querySelector('#main-image-overlay');
  if (mainImageOverlayEl) {
    close('main-image-overlay');
  }
}

onMounted(() => {
  window.addEventListener('popstate', onPopState);
});

onUnmounted(() => {
  window.removeEventListener('popstate', onPopState);
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '~@csgIcons/new/full-screen-dark-medium';
@keyframes product-images-cta-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.main-image-carousel {
  overflow: hidden;
  width: 100%;
  height: fit-content;
  position: relative;
  flex: 1 1 100%;

  @include breakpoint(large up) {
    width: 518px;
  }
}

.zoom-entry {
  position: absolute;
  right: rem-calc(16);
  top: rem-calc(64);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: rem-calc(4);
  border-radius: 50%;
  cursor: pointer;
  background-position: center;
  background-color: $white-overlay-color;

  animation-name: product-images-cta-fade;
  animation-delay: 0.1s;
  animation-fill-mode: backwards;
  animation-duration: 0.5s;

  &.zoom-first {
    top: rem-calc(16);
  }
}
.zoom-redesign {
  padding: rem-calc(8);
  background-color: $white-overlay-color-2;

  &:hover {
    background-color: $hover-color;
  }
}
</style>


